/**
 * The moving holiday decorations of a view, over the cached backdrop (seasonBackdrop.ts):
 *
 *  - Halloween: bats flapping across the sky and, now and then, a ghost peeking out from
 *    behind the ground, left of the player (where the level was already passed);
 *  - winter: falling snow, and snow on the tops of the ground and the blocks.
 *
 * Decoration only: nothing here reads or changes physics or timing. Bats, the ghost and the
 * far snowflakes are drawn behind the level; the snow caps lie inside the blocks, right under
 * their top edge (the surface a player lands on stays exactly where it is drawn). Everything
 * is a pure function of time and the camera (no particle state, no allocation per frame), the
 * snowfall thins out with the adaptive resolution, and nothing moves with reduced motion.
 */
import type { LevelData, LevelObject } from '@cube-rush/shared';
import type { Season } from '../season/season';
import { rgba } from './color';
import { bat } from './seasonBackdrop';
import type { ViewTransform } from './view';

/** What the decorations need of a frame. */
export interface DecorFrame {
  /** Animation clock, seconds. */
  time: number;
  season: Season;
  /** Adaptive resolution multiplier, 0.5..1 (busy frames get fewer snowflakes). */
  quality: number;
  reducedMotion: boolean;
}

/** A pseudo-random number 0..1 for an index (stable: the same flake every frame). */
function hash(i: number, k: number): number {
  const v = Math.sin(i * 127.1 + k * 311.7) * 43758.5453;
  return v - Math.floor(v);
}

function mod(value: number, m: number): number {
  return ((value % m) + m) % m;
}

function smooth(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

// ---- Halloween ----

const BAT_COLOR = rgba('#150a1d', 0.92);
const BAT_RIM = rgba('#ffb35a', 0.45);
/** Seconds between a ghost's appearances, and how long it stays. */
const GHOST_EVERY = 12;
const GHOST_FOR = 3.4;
const GHOST_FIRST = 7;
const GHOST_BODY = rgba('#f4efff', 0.78);
const GHOST_EYES = '#1a0d2a';

function drawBats(ctx: CanvasRenderingContext2D, view: ViewTransform, time: number): void {
  const count = view.w < 700 ? 3 : 5;
  const span = Math.min(30, Math.max(12, view.h * 0.05));
  for (let i = 0; i < count; i++) {
    // Each bat crosses the view in 7-12 s, then rests out of sight for a while.
    const crossing = 7 + hash(i, 1) * 5;
    const cycle = crossing * (1.4 + hash(i, 2) * 0.6);
    const t = mod(time + hash(i, 3) * cycle, cycle);
    if (t > crossing) continue;
    const progress = t / crossing;
    const rightward = i % 2 === 0;
    const reach = view.w + span * 2;
    const x = view.x - span + (rightward ? progress : 1 - progress) * reach;
    const y =
      view.y +
      view.h * (0.14 + hash(i, 4) * 0.32) +
      Math.sin(time * (1.3 + hash(i, 5)) + i) * view.h * 0.035;
    const lift = Math.sin(time * (13 + hash(i, 6) * 5) + i * 2.1);
    bat(ctx, x, y, span * (0.75 + hash(i, 7) * 0.5), lift, BAT_COLOR, BAT_RIM);
  }
}

/** A ghost rising from behind the floor line for a moment, left of the player. */
function drawPeekingGhost(ctx: CanvasRenderingContext2D, view: ViewTransform, time: number): void {
  // The first one comes a few seconds into the race, not during the countdown.
  const shifted = time - GHOST_FIRST;
  if (shifted < 0) return;
  const round = Math.floor(shifted / GHOST_EVERY);
  const t = shifted - round * GHOST_EVERY;
  if (t > GHOST_FOR) return;
  const floor = view.sy(0);
  // Only where the floor line is on screen, in its lower part (not in a ship's corridor high up).
  if (floor < view.y + view.h * 0.45 || floor > view.y + view.h - 4) return;
  const size = Math.min(48, Math.max(18, view.h * 0.085));
  const up = smooth(0, 0.8, t) * (1 - smooth(GHOST_FOR - 0.8, GHOST_FOR, t));
  if (up <= 0) return;
  const x = view.x + view.w * (0.07 + hash(round, 8) * 0.13) + Math.sin(t * 2.2) * size * 0.08;
  const y = floor + size * 0.75 - up * size * 1.15;
  const r = size / 2;
  const lean = Math.sin(t * 1.7) * 0.12;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(lean);
  ctx.globalAlpha = Math.min(1, up * 1.4);
  ctx.fillStyle = GHOST_BODY;
  ctx.beginPath();
  ctx.moveTo(-r, r * 1.3);
  ctx.lineTo(-r, 0);
  ctx.arc(0, 0, r, Math.PI, 0);
  ctx.lineTo(r, r * 1.3);
  ctx.fill();
  // Two big eyes looking towards the player, and a small «о».
  ctx.fillStyle = GHOST_EYES;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(side * r * 0.36 + r * 0.12, -r * 0.05, r * 0.16, r * 0.24, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.ellipse(r * 0.12, r * 0.42, r * 0.1, r * 0.14, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.globalAlpha = 1;
}

// ---- winter ----

const FLAKE_FAR = 'rgba(232, 242, 255, 0.55)';
const FLAKE_NEAR = 'rgba(255, 255, 255, 0.82)';
/** CSS px² of view per snowflake, at full quality. */
const FLAKE_AREA = 5200;
const MAX_FLAKES = 120;
/** Share of the flakes that fall in front of the level (bigger, faster, fewer). */
const NEAR_SHARE = 0.3;

function flakeCount(view: ViewTransform, quality: number): number {
  const count = Math.min(MAX_FLAKES, (view.w * view.h) / FLAKE_AREA);
  return Math.round(count * Math.min(1, Math.max(0.4, quality)));
}

/**
 * Falling snow: the far flakes (`near` false, behind the level) or the near ones. Flakes
 * sway, drift a little with the wind and slide past with the camera (parallax).
 */
function drawSnowfall(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  time: number,
  quality: number,
  near: boolean,
): void {
  const total = flakeCount(view, quality);
  const unit = Math.max(1, view.h / 400);
  const camera = view.left * view.ppb;
  const margin = 8 * unit;
  const width = view.w + margin * 2;
  const height = view.h + margin * 2;
  ctx.fillStyle = near ? FLAKE_NEAR : FLAKE_FAR;
  ctx.beginPath();
  for (let i = 0; i < total; i++) {
    if (hash(i, 9) < NEAR_SHARE !== near) continue;
    const depth = near ? 0.75 + hash(i, 10) * 0.25 : 0.25 + hash(i, 10) * 0.35;
    const fall = (14 + 34 * depth) * unit;
    const sway = Math.sin(time * (0.6 + hash(i, 11)) + i) * 10 * unit * depth;
    const x =
      view.x -
      margin +
      mod(hash(i, 12) * width - camera * depth * 0.5 - time * 6 * unit * depth + sway, width);
    const y = view.y - margin + mod(hash(i, 13) * height + time * fall, height);
    const size = (near ? 1.6 + hash(i, 14) * 1.4 : 0.8 + hash(i, 14) * 0.9) * unit;
    if (near) {
      ctx.moveTo(x + size, y);
      ctx.arc(x, y, size, 0, Math.PI * 2);
    } else {
      ctx.rect(x - size, y - size, size * 2, size * 2);
    }
  }
  ctx.fill();
}

/** A stretch of a block or ground top that is open to the sky (world blocks). */
export interface CapSegment {
  x1: number;
  x2: number;
  /** Height of the top surface. */
  y: number;
}

/**
 * The objects the snow caps know, by type, with their fields: the level format of today,
 * where nothing moves. A block with other fields (a moving one, say) gets no snow, and an
 * object the caps do not know keeps the snow well away (see snowCapSegments).
 */
const KNOWN_FIELDS: Readonly<Record<string, ReadonlySet<string>>> = {
  block: new Set(['type', 'x', 'y', 'w', 'h', 'style']),
  spike: new Set(['type', 'x', 'y', 'w', 'h', 'dir']),
  pad: new Set(['type', 'x', 'y', 'w', 'h', 'color', 'dir']),
  orb: new Set(['type', 'x', 'y', 'w', 'h', 'color']),
  portal: new Set(['type', 'x', 'y', 'w', 'h', 'portal']),
  pickup: new Set(['type', 'x', 'y', 'w', 'h', 'boost']),
};
/** How deep a cap reaches under its top, blocks (drawSnowCaps' inset and depth, with room). */
const CAP_BAND = 0.3;
/** No snow this close to an object the caps do not know (it may move), blocks. */
const STRANGER_REACH = 3;
const EPS = 1e-6;

function isKnown(o: LevelObject): boolean {
  const fields = KNOWN_FIELDS[o.type];
  if (!fields) return false;
  for (const key of Object.keys(o)) if (!fields.has(key)) return false;
  return true;
}

function isPlainBlock(o: LevelObject): o is LevelObject & { type: 'block' } {
  return o.type === 'block' && isKnown(o);
}

/**
 * The open tops of a level's ground and blocks, sorted by x: where snow can lie. A top
 * covered by another block (a stack, a wall) gets none, slopes and ceilings get none, and
 * the finish line stays clear. The caps are drawn over the level's objects, so they also
 * leave out whatever reaches into the band under a top (a spike sunk into it), and keep
 * STRANGER_REACH away from objects they do not know (a moving one: wherever it goes). The
 * ground under the start and past the finish runs on forever, as the level art draws it.
 */
export function snowCapSegments(level: LevelData): CapSegment[] {
  const objects = level.objects;
  const solids = objects.filter((o) => o.type === 'block');
  let runIn: LevelObject | null = null;
  let runOut: LevelObject | null = null;
  for (const o of objects) {
    if (o.type !== 'block' || o.style !== 'ground') continue;
    if (!runIn || o.x < runIn.x) runIn = o;
    if (!runOut || o.x + o.w > runOut.x + runOut.w) runOut = o;
  }
  if (runIn && runIn.x > level.start.x) runIn = null;
  if (runOut && runOut.x + runOut.w < level.length) runOut = null;

  const raw: CapSegment[] = [];
  for (const o of objects) {
    if (!isPlainBlock(o) || o.style === 'slope' || o.style === 'ceiling') continue;
    const top = o.y + o.h;
    let pieces: Array<[number, number]> = [
      [
        o === runIn ? Number.NEGATIVE_INFINITY : o.x,
        o === runOut ? Number.POSITIVE_INFINITY : o.x + o.w,
      ],
    ];
    // Whatever sits on (or reaches over) this top hides it there.
    for (const b of solids) {
      if (b === o || b.y > top + EPS || b.y + b.h <= top + EPS) continue;
      pieces = subtract(pieces, b.x, b.x + b.w);
      if (pieces.length === 0) break;
    }
    // Nothing else may lie in the band under the top: the snow would be painted over it.
    for (const other of objects) {
      if (pieces.length === 0) break;
      if (other === o || isPlainBlock(other)) continue;
      const reach = isKnown(other) ? 0 : STRANGER_REACH;
      if (other.y - reach >= top - EPS || other.y + other.h + reach <= top - CAP_BAND) continue;
      pieces = subtract(pieces, other.x - reach, other.x + other.w + reach);
    }
    // The finish line's chequered strip.
    pieces = subtract(pieces, level.length - 0.5, level.length + 0.5);
    for (const [x1, x2] of pieces) if (x2 - x1 > 0.05) raw.push({ x1, x2, y: top });
  }
  raw.sort((a, b) => a.x1 - b.x1 || a.y - b.y);
  // Neighbours at one height become one cap (its wavy edge runs on across the seam).
  const merged: CapSegment[] = [];
  for (const segment of raw) {
    const last = merged.find((m) => Math.abs(m.y - segment.y) < EPS && m.x2 >= segment.x1 - EPS);
    if (last) last.x2 = Math.max(last.x2, segment.x2);
    else merged.push({ ...segment });
  }
  return merged.sort((a, b) => a.x1 - b.x1);
}

/** `pieces` minus the interval [a, b]. */
function subtract(pieces: Array<[number, number]>, a: number, b: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const [x1, x2] of pieces) {
    if (b <= x1 || a >= x2) {
      out.push([x1, x2]);
      continue;
    }
    if (a > x1) out.push([x1, a]);
    if (b < x2) out.push([b, x2]);
  }
  return out;
}

const capCache = new WeakMap<LevelData, CapSegment[]>();

function capsOf(level: LevelData): CapSegment[] {
  let caps = capCache.get(level);
  if (!caps) {
    caps = snowCapSegments(level);
    capCache.set(level, caps);
  }
  return caps;
}

/** Steps (blocks) at which the wavy lower edge of a cap is drawn. */
const CAP_STEP = 0.25;
const SNOW = 'rgba(240, 247, 255, 0.95)';
const SNOW_SHADE = 'rgba(176, 206, 240, 0.9)';

/** Thickness of the snow at world x, 0.55..1 of the full depth (lumpy, stable as it scrolls). */
function capWave(x: number): number {
  return 0.72 + 0.18 * Math.sin(x * 2.3) + 0.1 * Math.sin(x * 5.1 + 1.7);
}

/** Snow on the open tops in view: a band right under the top edge with a lumpy lower side. */
function drawSnowCaps(ctx: CanvasRenderingContext2D, view: ViewTransform, level: LevelData): void {
  const caps = capsOf(level);
  if (caps.length === 0) return;
  const from = view.left - 1;
  const to = view.right + 1;
  // Clear of the glowing top edge the level art draws (its line is up to ~0.08 blocks wide).
  const inset = Math.max(1.5, view.len(0.045));
  const depth = Math.max(3, view.len(0.2));
  const viewTop = view.y - depth;
  const viewBottom = view.y + view.h + depth;
  for (let pass = 0; pass < 2; pass++) {
    ctx.beginPath();
    let any = false;
    for (const cap of caps) {
      if (cap.x1 > to) break;
      if (cap.x2 < from) continue;
      const sy = view.sy(cap.y);
      if (sy < viewTop || sy > viewBottom) continue;
      const x1 = Math.max(cap.x1, from);
      const x2 = Math.min(cap.x2, to);
      // A thin shadow line under the snow first, then the snow over most of it.
      const k = pass === 0 ? 1 : 0.82;
      const top = sy + inset;
      const left = view.sx(x1) + inset;
      const right = view.sx(x2) - inset;
      if (right <= left) continue;
      ctx.moveTo(left, top);
      ctx.lineTo(right, top);
      ctx.lineTo(right, top + depth * k * capWave(x2) * 0.6);
      // The lumpy lower side at fixed world steps (it scrolls with the level, never shimmers).
      for (let x = Math.floor(x2 / CAP_STEP) * CAP_STEP; x > x1; x -= CAP_STEP) {
        const sx = view.sx(x);
        if (sx < right && sx > left) ctx.lineTo(sx, top + depth * k * capWave(x));
      }
      ctx.lineTo(left, top + depth * k * capWave(x1) * 0.6);
      ctx.closePath();
      any = true;
    }
    if (!any) return;
    ctx.fillStyle = pass === 0 ? SNOW_SHADE : SNOW;
    ctx.fill();
  }
}

// ---- the view's decorations ----

export class SeasonDecor {
  /** Behind the level, over the backdrop: bats, the peeking ghost, the far snowflakes. */
  drawBehind(ctx: CanvasRenderingContext2D, view: ViewTransform, frame: DecorFrame): void {
    if (frame.reducedMotion) return;
    if (frame.season === 'halloween') {
      drawBats(ctx, view, frame.time);
      drawPeekingGhost(ctx, view, frame.time);
    } else if (frame.season === 'winter') {
      drawSnowfall(ctx, view, frame.time, frame.quality, false);
    }
  }

  /** Over the level, under the racers: snow on the tops, the near snowflakes. */
  drawOnLevel(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    frame: DecorFrame,
    level: LevelData,
  ): void {
    if (frame.season !== 'winter') return;
    drawSnowCaps(ctx, view, level);
    if (!frame.reducedMotion) drawSnowfall(ctx, view, frame.time, frame.quality, true);
  }
}
